import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';

import { SchedulerChatUI } from '../../Chat/SchedulerChatUI';
import dayjs from 'dayjs';
import { useAIScheduler, useDailyPrefSchedulerChat } from '../../../hooks';
import { selectedDateAtom, useAssistantChat, useAuthContext } from '../../../data-access';

interface AssistantChatSchedulerProps {
  onClear: () => void;
  setProcessedIds: (ids: string[]) => void;
  containerHeight?: number;
}

const AssistantChatScheduler: React.FC<AssistantChatSchedulerProps> = ({
  onClear,
  setProcessedIds,
  containerHeight,
}) => {
  const {
    handleRunAIScheduler,
    handleAdjustAIScheduler,
    resetAiScheduler,
    aiScheduledActionsEvents,
    planResultSourceById,
    isWeekPlanningComplete,
    handleDiscardEvent,
    handleDiscardEvents,
    handleAcceptAllEvents,
    handleAcceptAIScheduleEvent,
  } = useAIScheduler();
  const [selectedDate, setSelectedDate] = useRecoilState(selectedDateAtom);
  const { user } = useAuthContext();
  const { schedulerScope } = useAssistantChat();
  const isWeekScope = schedulerScope === 'week';

  const getDefaultWeekRange = () => {
    const today = dayjs().startOf('day');
    const daysUntilFriday = (5 - today.day() + 7) % 7;
    return {
      startDate: today,
      endDate: today.add(daysUntilFriday, 'day').startOf('day'),
    };
  };
  const [weekRangeEndDate, setWeekRangeEndDate] = useState(() => getDefaultWeekRange().endDate);

  useEffect(() => {
    if (!isWeekScope) {
      return;
    }
    const defaultRange = getDefaultWeekRange();
    setSelectedDate(defaultRange.startDate);
    setWeekRangeEndDate(defaultRange.endDate);
  }, [isWeekScope, setSelectedDate]);

  // Use the scheduler chat hook
  const schedulerChatHook = useDailyPrefSchedulerChat({
    selectedDate,
    rangeEndDate: isWeekScope ? weekRangeEndDate : undefined,
    hasTodoInited: !!user?.todoTasksBoardIds?.length,
    schedulerScope,
    handleRunAIScheduler,
    onScheduleForTomorrow: () => {
      const targetDay = dayjs(selectedDate).add(1, 'day');
      setSelectedDate(targetDay);
    },
  });

  const hasScheduledActions = useMemo(() => {
    if (isWeekScope) {
      return isWeekPlanningComplete && aiScheduledActionsEvents.length > 0;
    }
    return aiScheduledActionsEvents.length > 0;
  }, [aiScheduledActionsEvents, isWeekPlanningComplete, isWeekScope]);

  const handleClear = () => {
    setProcessedIds([]);
    onClear();
    resetAiScheduler();
  };

  return (
    <SchedulerChatUI
      messages={schedulerChatHook.visibleMessages}
      onRunScheduler={schedulerChatHook.handleRun}
      onCustomRuleInput={schedulerChatHook.handleCustomRuleInput}
      isInitializing={schedulerChatHook.isInitializing}
      canSchedule={schedulerChatHook.canSchedule}
      currentMessageIndex={schedulerChatHook.currentMessageIndex}
      totalMessages={schedulerChatHook.totalMessages}
      isTyping={schedulerChatHook.isTyping}
      isV5Flow={schedulerChatHook.isV5Flow}
      hasScheduledActions={hasScheduledActions}
      showRefresh
      onClearPress={handleClear}
      onAdjustScheduler={handleAdjustAIScheduler}
      scheduledEvents={aiScheduledActionsEvents}
      planResultSourceById={planResultSourceById}
      onDiscardScheduledEvent={handleDiscardEvent}
      onDiscardAllScheduledEvents={handleDiscardEvents}
      onAcceptScheduledEvent={handleAcceptAIScheduleEvent}
      onSaveScheduledEvents={async () => {
        await handleAcceptAllEvents();
      }}
      containerHeight={containerHeight}
      selectedDate={selectedDate}
      onSelectedDateChange={(date) => {
        if (isWeekScope) {
          const nextStartDate = date.startOf('day');
          setSelectedDate(nextStartDate);
          setWeekRangeEndDate((prevEndDate) =>
            prevEndDate?.isBefore(nextStartDate, 'day') ? nextStartDate : prevEndDate
          );
          return;
        }
        setSelectedDate(date);
      }}
      onRangeDateChange={(startDate, endDate) => {
        setSelectedDate(startDate.startOf('day'));
        setWeekRangeEndDate(endDate.startOf('day'));
      }}
      rangeEndDate={isWeekScope ? weekRangeEndDate : undefined}
      tasks={schedulerChatHook.tasksPrefItems}
      routines={schedulerChatHook.routinePrefItem}
      targetDate={selectedDate.format('YYYY-MM-DD')}
      onTaskSelectionChange={schedulerChatHook.onTaskSelection}
      onTaskChange={schedulerChatHook.onTaskChange}
      onRoutineSelectionChange={schedulerChatHook.onRoutineSelection}
      onRoutineChange={schedulerChatHook.onRoutineChange}
      onRoutineAdd={schedulerChatHook.onRoutineAdd}
      onRoutineDelete={schedulerChatHook.onRoutineDelete}
      tasksLoading={schedulerChatHook.isPreferenceLoading}
      routineLoading={schedulerChatHook.isRoutineLoading}
      workHours={schedulerChatHook.workHourPrefItem}
      onWorkHourChange={schedulerChatHook.onWorkHourChange}
      workHoursLoading={schedulerChatHook.isPreferenceLoading}
      existingRoutineEvents={schedulerChatHook.existingRoutineEvents}
      scheduledRoutineActivityIds={schedulerChatHook.scheduledRoutineActivityIds}
      schedulerScope={schedulerScope}
      defaultRunMessage={
        isWeekScope
          ? 'Plan my week from Monday (or today) through Friday. Prioritize overdue and week tasks.'
          : undefined
      }
    />
  );
};

export default AssistantChatScheduler;
